import { cn } from "@/lib/utils";
import { createContext, forwardRef, useContext, useState } from "react";

const ExpandableContext = createContext({
  isOpen: false,
  setIsOpen: (isOpen: boolean) => {},
});

type ExpandableProps = {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const Expandable = ({
  children,
  open,
  defaultOpen,
  onOpenChange,
}: ExpandableProps) => {
  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(
    defaultOpen ?? false,
  );
  const resolvedOpen = isControlled ? (open as boolean) : uncontrolledOpen;

  const setIsOpen = (next: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(next);
    }
    onOpenChange?.(next);
  };

  return (
    <ExpandableContext.Provider value={{ isOpen: resolvedOpen, setIsOpen }}>
      {children}
    </ExpandableContext.Provider>
  );
};

type TriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
  children: React.ReactNode;
};

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ children, className, onClick, ...props }, ref) => {
    const { isOpen, setIsOpen } = useContext(ExpandableContext);
    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      onClick?.(e);
      if (e.defaultPrevented) return;
      setIsOpen(!isOpen);
    };
    return (
      <button
        ref={ref}
        type="button"
        aria-expanded={isOpen}
        data-state={isOpen ? "open" : "closed"}
        onClick={handleClick}
        className={cn("cursor-pointer", className)}
        {...props}
      >
        {children}
      </button>
    );
  },
);

type ContentProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string;
  children: React.ReactNode;
};

const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ children, className, ...props }, ref) => {
    const { isOpen } = useContext(ExpandableContext);
    if (!isOpen) return null;
    return (
      <div
        ref={ref}
        data-state={isOpen ? "open" : "closed"}
        className={cn("overflow-hidden", className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Expandable.Trigger = Trigger;
Expandable.Content = Content;

export default Expandable;
